import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTier } from "@/context/TierContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Palette, Sparkles, Loader2, Lock, ImageIcon } from "lucide-react";
import { toast } from "sonner";

const DEFAULTS = { accent: "#0047FF", include_cover_photo: false, custom_footer_line: "" };

export function ReportPersonalization() {
  const { user, setUser } = useAuth();
  const { isPremium, openUpgrade } = useTier();
  const initial = { ...DEFAULTS, ...(user?.report_prefs || {}), custom_footer_line: user?.report_prefs?.custom_footer_line || "" };

  const [prefs, setPrefs] = useState(initial);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setPrefs((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/me/report-prefs", {
        accent: prefs.accent,
        include_cover_photo: prefs.include_cover_photo,
        custom_footer_line: prefs.custom_footer_line || "",
      });
      setUser((u) => (u ? { ...u, report_prefs: data.report_prefs } : u));
      toast.success("Report style saved — it'll apply to your next Evidence PDF.");
    } catch (e) {
      console.error("report-prefs save failed", e);
      if (e?.response?.status === 402) openUpgrade("report_styling");
      else toast.error("Couldn't save — please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full rounded-2xl border border-[#E2E8F0] bg-white p-6" data-testid="report-personalization">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-semibold text-navy flex items-center gap-2">
          <Palette size={18} className="text-brand" /> Evidence PDF style
        </h2>
        <span className="inline-flex items-center gap-1 rounded-full bg-brand/10 px-2.5 py-1 text-xs font-semibold text-brand">
          <Sparkles size={12} /> Plus
        </span>
      </div>
      <p className="mt-2 text-sm text-[#475569]">
        Personalize the Evidence Package you share with your landlord — accent color, a cover photo, and a custom footer line.
      </p>

      <div className={`mt-5 space-y-5 ${isPremium ? "" : "opacity-60 pointer-events-none select-none"}`} aria-disabled={!isPremium}>
        {/* Accent color */}
        <div>
          <Label className="text-sm font-semibold text-navy">Accent color</Label>
          <div className="mt-2 flex items-center gap-3">
            <input
              type="color"
              data-testid="accent-color-picker"
              value={prefs.accent}
              onChange={(e) => set("accent", e.target.value.toUpperCase())}
              className="h-10 w-14 cursor-pointer rounded-lg border border-[#E2E8F0] bg-white p-1"
            />
            <Input
              data-testid="accent-hex-input"
              value={prefs.accent}
              onChange={(e) => set("accent", e.target.value.toUpperCase())}
              className="w-32 rounded-xl font-mono"
              maxLength={7}
            />
            <span className="text-xs text-[#94A3B8]">Used on the cover divider.</span>
          </div>
        </div>

        {/* Cover photo toggle */}
        <div className="flex items-center justify-between rounded-xl bg-[#FAF7F2] border border-[#E2E8F0] px-4 py-3">
          <div className="flex items-center gap-3">
            <ImageIcon size={18} className="text-[#94A3B8]" />
            <div>
              <p className="text-sm font-semibold text-navy">Cover photo</p>
              <p className="text-xs text-[#94A3B8]">Show your first room photo on the cover page.</p>
            </div>
          </div>
          <Switch
            data-testid="cover-photo-switch"
            checked={!!prefs.include_cover_photo}
            onCheckedChange={(v) => set("include_cover_photo", v)}
          />
        </div>

        {/* Custom footer line */}
        <div>
          <Label className="text-sm font-semibold text-navy">Custom footer line</Label>
          <Input
            data-testid="footer-line-input"
            value={prefs.custom_footer_line}
            onChange={(e) => set("custom_footer_line", e.target.value.slice(0, 120))}
            placeholder="e.g. Prepared by Jane Doe · janedoe@email.com"
            className="mt-2 rounded-xl"
            maxLength={120}
          />
          <p className="mt-1 text-xs text-[#94A3B8]">{(prefs.custom_footer_line || "").length}/120 · appears at the bottom of every page.</p>
        </div>

        <Button data-testid="save-report-prefs-btn" onClick={save} disabled={saving}
          className="rounded-xl bg-brand hover:bg-brand-hover text-white">
          {saving ? <><Loader2 size={16} className="mr-2 animate-spin" /> Saving…</> : "Save report style"}
        </Button>
      </div>

      {!isPremium && (
        <div className="mt-5 flex items-center justify-between gap-4 rounded-xl border border-brand/20 bg-brand/5 px-4 py-3" data-testid="report-prefs-upgrade">
          <p className="text-sm text-[#334155] flex items-center gap-2">
            <Lock size={16} className="text-brand shrink-0" /> Report styling is a TenantSeal Plus feature.
          </p>
          <Button data-testid="report-prefs-upgrade-btn" onClick={() => openUpgrade("report_styling")}
            className="rounded-xl bg-brand hover:bg-brand-hover text-white shrink-0">
            Upgrade to TenantSeal Plus
          </Button>
        </div>
      )}
    </div>
  );
}
